import { softwareList } from '@/mocks/software';
import { categories } from '@/mocks/categories';
import type { Platform, Software } from '@/types/software';

export const platformIcons: Record<Platform, string> = {
  Windows: 'ri-windows-fill',
  Android: 'ri-android-fill',
  iOS: 'ri-apple-fill',
  Mac: 'ri-apple-fill',
  Web: 'ri-global-line',
};

export const platformOptions: Platform[] = ['Windows', 'Android', 'iOS', 'Mac', 'Web'];

export const typeOptions = ['播放器', '解码器', '辅助工具'] as const;

export const sortOptions = [
  { value: 'downloads', label: '按下载量' },
  { value: 'updateDate', label: '按更新时间' },
  { value: 'name', label: '按名称' },
] as const;

export type SortValue = (typeof sortOptions)[number]['value'];

export function formatDownloads(value: number): string {
  if (value >= 100000000) return `${(value / 100000000).toFixed(1)}亿`;
  if (value >= 10000) return `${Math.round(value / 10000)}万`;
  return String(value);
}

export function getSoftwareById(id: string): Software | undefined {
  return softwareList.find((item) => item.id === id);
}

export function getCategoryById(id: string): (typeof categories)[number] | undefined {
  return categories.find((item) => item.id === id);
}

export function getSoftwareByCategory(categoryId: string): Software[] {
  return softwareList.filter((item) => item.categoryId === categoryId);
}

export function getRecommended(target: Software, limit = 4): Software[] {
  const sameCategory = softwareList.filter(
    (item) => item.id !== target.id && item.categoryId === target.categoryId,
  );
  const others = softwareList.filter(
    (item) => item.id !== target.id && item.categoryId !== target.categoryId,
  );
  return [...sameCategory, ...others].slice(0, limit);
}

export interface FilterParams {
  keyword?: string;
  platforms?: Platform[];
  type?: string;
  updatedWithin?: string;
  sort?: SortValue;
}

export function filterSoftware(list: Software[], params: FilterParams): Software[] {
  const { keyword, platforms, type, updatedWithin, sort = 'downloads' } = params;
  const kw = keyword?.trim().toLowerCase();

  let result = list.filter((item) => {
    if (platforms && platforms.length > 0) {
      const hit = item.platforms.some((p) => platforms.includes(p));
      if (!hit) return false;
    }
    if (type && item.type !== type) return false;
    if (updatedWithin) {
      const limit = Number(updatedWithin);
      const diff = (Date.now() - new Date(item.updateDate).getTime()) / 86400000;
      if (diff > limit) return false;
    }
    if (kw) {
      const haystack = `${item.name} ${item.enName} ${item.summary} ${item.description} ${item.developer}`.toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    return true;
  });

  result = [...result].sort((a, b) => {
    if (sort === 'downloads') return b.downloads - a.downloads;
    if (sort === 'updateDate') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    return a.name.localeCompare(b.name, 'zh-Hans-CN');
  });

  return result;
}